// Report and chart data sources saved per merchant (FIG-345 · UC-229),
// stored in organisations.report_data_sources. Shared by the client and the
// update-merchant-report-sources-v1/-v2 edge functions, so it has no imports.
//
// Each row stores "All Sources", one option value or (FIG-409 · UC-233) several
// option values of that row:
// - variants / salesHistory: connection ids from the report's source list;
// - dailyNervTrends: chart source values ('shopify', 'amazon', 'amazon_csv_import', …).
// Saved choices are canonical (toReportSourceChoice): every option → 'all' (so
// sources connected later are included), one → 'source', otherwise 'sources'.

export type ReportSourceKey = 'variants' | 'salesHistory' | 'dailyNervTrends';

/** Most ids a 'sources' choice may hold (validated client- and server-side). */
export const MAX_REPORT_SOURCE_IDS = 50;
const MAX_REPORT_SOURCE_ID_LENGTH = 200;

export const REPORT_SOURCE_KEYS: readonly ReportSourceKey[] = ['variants', 'salesHistory', 'dailyNervTrends'];

export const REPORT_SOURCE_LABELS: Record<ReportSourceKey, string> = {
  variants: 'Variants data source',
  salesHistory: 'Sales History data source',
  dailyNervTrends: 'Daily NERV Trends data source',
};

export type ReportSourceChoice =
  | { kind: 'all' }
  | { kind: 'source'; id: string }
  | { kind: 'sources'; ids: string[] };

export type ReportDataSources = Partial<Record<ReportSourceKey, ReportSourceChoice>>;

export function isReportSourceKey(value: unknown): value is ReportSourceKey {
  return typeof value === 'string' && (REPORT_SOURCE_KEYS as readonly string[]).includes(value);
}

const isSourceId = (value: unknown): value is string =>
  typeof value === 'string' && value.length > 0 && value.length <= MAX_REPORT_SOURCE_ID_LENGTH;

export function isReportSourceChoice(value: unknown): value is ReportSourceChoice {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return false;
  const choice = value as Record<string, unknown>;
  if (choice.kind === 'all') return true;
  if (choice.kind === 'source') return isSourceId(choice.id);
  if (choice.kind === 'sources') {
    const ids = choice.ids;
    return Array.isArray(ids)
      && ids.length > 0
      && ids.length <= MAX_REPORT_SOURCE_IDS
      && ids.every(isSourceId)
      && new Set(ids).size === ids.length;
  }
  return false;
}

/** A clean copy of a valid choice (drops any extra stored fields). */
function copyChoice(choice: ReportSourceChoice): ReportSourceChoice {
  if (choice.kind === 'all') return { kind: 'all' };
  if (choice.kind === 'source') return { kind: 'source', id: choice.id };
  return { kind: 'sources', ids: [...choice.ids] };
}

/** Reads the stored jsonb, dropping anything malformed instead of throwing. */
export function parseReportDataSources(value: unknown): ReportDataSources {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const result: ReportDataSources = {};
  for (const key of REPORT_SOURCE_KEYS) {
    const choice = (value as Record<string, unknown>)[key];
    if (isReportSourceChoice(choice)) result[key] = copyChoice(choice);
  }
  return result;
}

/**
 * The canonical choice for a set of ticked options (FIG-409 · UC-233):
 * every option ticked → 'all' (stays dynamic, AC4); one → 'source' (the
 * pre-FIG-409 shape, AC5); otherwise → 'sources'. Returns null for an empty
 * selection, which can't be saved (AC2). Ids not in `allIds` are ignored.
 */
export function toReportSourceChoice(selectedIds: readonly string[], allIds: readonly string[]): ReportSourceChoice | null {
  const ids = allIds.filter((id, index) => selectedIds.includes(id) && allIds.indexOf(id) === index);
  if (ids.length === 0) return null;
  const optionCount = new Set(allIds).size;
  if (ids.length === optionCount && optionCount > 1) return { kind: 'all' };
  if (ids.length === 1) return { kind: 'source', id: ids[0] };
  return { kind: 'sources', ids };
}

/** The ids a saved choice names (empty for 'all'). */
export function reportSourceChoiceIds(choice: ReportSourceChoice | undefined): string[] {
  if (!choice || choice.kind === 'all') return [];
  return choice.kind === 'source' ? [choice.id] : [...choice.ids];
}

/** The connection fields the source derivations read. */
export interface SourceConnectionFields {
  id?: string | null;
  connectionId?: string | null;
  name?: string | null;
  service?: string | null;
  type?: string | null;
  import_type?: string | null;
  ecommerce_subtype?: string | null;
}

/**
 * The Daily NERV Trends chart's option for a connection: a platform value
 * ('shopify', 'amazon') or '<platform>_csv_import', or null when the chart
 * can't use it. Used by the chart, the Settings row and the edge function.
 */
export function dailyNervSourceFor(conn: SourceConnectionFields | null | undefined): { value: string; label: string } | null {
  if (!conn || conn.service === 'file') return null;
  if (conn.type === 'CSV' || conn.connectionId?.startsWith('csv-import-')) {
    if (!conn.ecommerce_subtype) return null;
    const subtype = conn.ecommerce_subtype.toLowerCase();
    return { value: `${subtype}_csv_import`, label: `${subtype.charAt(0).toUpperCase() + subtype.slice(1)} CSV Import` };
  }
  const service = (conn.service || '').toLowerCase();
  if (service.includes('shopify')) return { value: 'shopify', label: conn.name || 'Shopify' };
  if (service.includes('amazon')) return { value: 'amazon', label: conn.name || 'Amazon' };
  return null;
}
